import { PRIORIDADES_TAREA, type PrioridadTarea, type SemanaPublic, type TareaPublic } from '@app/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '../../../components/ui/form-field';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { tareasApi } from '../api/tareas.api';
import { dentroDeSemana, diaMes } from '../lib/fechas';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = alta; una tarea = edición. */
  tarea: TareaPublic | null;
  /** La semana que se planifica: da el rango válido del vencimiento. */
  semana: SemanaPublic | undefined;
}

const VACIO = { titulo: '', descripcion: '', prioridad: 'MEDIA' as PrioridadTarea, venceEl: '' };

// ciclo-tareas, R5: un solo diálogo para crear y editar — con "Eliminar" solo en
// modo edición. Mismo patrón que CampanaCrearDialog (form controlado + sonner).
// ciclo-tareas-deadline, R5: el vencimiento es obligatorio y solo acepta días
// de la semana que se planifica (el backend lo vuelve a controlar).
export function FormularioTarea({ open, onOpenChange, tarea, semana }: Props): JSX.Element {
  const es = useI18n();
  const t = es.tareas.dialogo;
  const queryClient = useQueryClient();
  const [form, setForm] = useState(VACIO);
  const [error, setError] = useState<string | null>(null);
  const [confirmarEliminarAbierto, setConfirmarEliminarAbierto] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        tarea
          ? {
              titulo: tarea.titulo,
              descripcion: tarea.descripcion ?? '',
              prioridad: tarea.prioridad,
              venceEl: tarea.venceEl ?? '',
            }
          : VACIO,
      );
      setError(null);
    }
  }, [open, tarea]);

  function invalidar(): void {
    void queryClient.invalidateQueries({ queryKey: qk.tareas.all });
    void queryClient.invalidateQueries({ queryKey: qk.tareas.estadisticas });
  }

  const crear = useMutation({
    mutationFn: () =>
      tareasApi.crear({
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim() || null,
        prioridad: form.prioridad,
        venceEl: form.venceEl,
      }),
    onSuccess: () => {
      invalidar();
      toast.success(t.exitoCreada);
      onOpenChange(false);
    },
    onError: (e) => setError(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  const editar = useMutation({
    mutationFn: () =>
      tareasApi.editar(tarea!.id, {
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim() || null,
        prioridad: form.prioridad,
        venceEl: form.venceEl,
      }),
    onSuccess: () => {
      invalidar();
      toast.success(t.exitoEditada);
      onOpenChange(false);
    },
    onError: (e) => setError(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  const eliminar = useMutation({
    mutationFn: () => tareasApi.eliminar(tarea!.id),
    onSuccess: () => {
      invalidar();
      toast.success(t.exitoEliminada);
      setConfirmarEliminarAbierto(false);
      onOpenChange(false);
    },
    onError: (e) => setError(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  function enviar(): void {
    setError(null);
    if (form.titulo.trim().length === 0) {
      setError(t.errorTitulo);
      return;
    }
    if (semana && !dentroDeSemana(form.venceEl || null, semana.inicio, semana.fin)) {
      setError(t.errorVence.replace('{inicio}', diaMes(semana.inicio)).replace('{fin}', diaMes(semana.fin)));
      return;
    }
    if (tarea) editar.mutate();
    else crear.mutate();
  }

  const guardando = crear.isPending || editar.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tarea ? t.tituloEditar : t.tituloCrear}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            enviar();
          }}
          className="flex flex-col gap-4"
          noValidate
        >
          {error && <p className="text-sm text-danger">{error}</p>}

          <FormField label={t.titulo} htmlFor="tarea-titulo">
            <Input
              id="tarea-titulo"
              value={form.titulo}
              onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
            />
          </FormField>

          <FormField label={t.descripcionOpcional} htmlFor="tarea-descripcion">
            <Textarea
              id="tarea-descripcion"
              rows={2}
              value={form.descripcion}
              onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
            />
          </FormField>

          <FormField label={t.vence} htmlFor="tarea-vence">
            <Input
              id="tarea-vence"
              type="date"
              required
              min={semana?.inicio}
              max={semana?.fin}
              value={form.venceEl}
              onChange={(e) => setForm((f) => ({ ...f, venceEl: e.target.value }))}
            />
          </FormField>

          <FormField label={es.tareas.prioridad.etiqueta} htmlFor="tarea-prioridad">
            <div id="tarea-prioridad" className="flex gap-2">
              {PRIORIDADES_TAREA.map((p) => (
                <Button
                  key={p}
                  type="button"
                  size="sm"
                  variant={form.prioridad === p ? 'default' : 'outline'}
                  onClick={() => setForm((f) => ({ ...f, prioridad: p }))}
                >
                  {es.tareas.prioridad[p]}
                </Button>
              ))}
            </div>
          </FormField>

          <DialogFooter className="justify-between">
            {tarea ? (
              <Button
                type="button"
                variant="danger"
                disabled={eliminar.isPending}
                onClick={() => setConfirmarEliminarAbierto(true)}
              >
                {eliminar.isPending ? t.eliminando : t.eliminar}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t.cancelar}
              </Button>
              <Button type="submit" disabled={guardando}>
                {guardando ? t.guardando : tarea ? t.guardar : t.crear}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>

      {tarea && (
        <ConfirmDialog
          open={confirmarEliminarAbierto}
          onOpenChange={setConfirmarEliminarAbierto}
          titulo={t.eliminar}
          descripcion={t.confirmarEliminar}
          confirmar={t.eliminar}
          confirmando={t.eliminando}
          cancelar={t.cancelar}
          pendiente={eliminar.isPending}
          variant="danger"
          onConfirmar={() => eliminar.mutate()}
        />
      )}
    </Dialog>
  );
}
